/**
 * field-factory.js — 表單欄位 markup factory（純 JS，不是 custom element）
 *
 * 把目前各 admin edit 頁手寫的 .form-field atom 收斂成宣告式 factory，產出與手寫
 * **byte-level 等價**（零新 class、CSS 不用改）。階段 0 只建 factory 本身，不改任何頁面；
 * 未來頁面改用 factory 時行為與現狀一致。
 *
 * 三個產出 API：
 *   formField(spec)      → HTMLElement（DOM 節點，動態插入用）
 *   formFieldHTML(spec)  → string（innerHTML 模板用；所有插入值先 escapeHtml 防 XSS）
 *   formFieldGroup(spec) → HTMLElement（.check-grid[role=group] checkbox 群）
 *
 * 對齊現有 atom（見 src/styles/components/form-field.css 與各 admin edit HTML）：
 *   <div class="form-field [form-field--required]">
 *     <label class="form-field__label" for=ID>…</label>
 *     <input|select|textarea class="form-field__input [--select|--textarea|--mono]" id name …>
 *     <p class="form-field__hint" id="ID-hint">…</p>          （有 hint 才產）
 *     <p class="form-field__error" id="ID-error" role="alert" hidden></p>  （恆產，引擎填字）
 *   </div>
 *
 * rules 投影原則：
 *   - 自訂規則（tw-mobile / tw-tel / license-no / approval-no / checkbox-min …）只寫 data-rules，
 *     factory 不解語義，交 field-rules.js 規則層在 submit / blur 時解。
 *   - 能對應 HTML5 原生的（email / url / number min/max/step / required / minlength / maxlength）
 *     同時補原生屬性，讓 validity.* 路徑也吃（與規則層雙保險）。
 *
 * 載入：純 markup factory，不 auto-init。由 admin-editor.js / admin-form-edit.js /
 *   pages/doctors-modal-form.js import 使用（皆在 /src/admin.js 鏈）；自訂規則交 field-rules.js 解。
 */

/* escapeHtml — 收進共用 util（原 render-cards.js / admin-table.js / admin-editor.js / 本檔
 * 四處 verbatim 同式：String(s).replace(/[&<>"']/g, map)，B-10 去重收斂為單一來源）。 */
import { escapeHtml } from '../utils/dom.js';

/* type → input 標籤上的 type 屬性（select/textarea/switch/file 走各自分支，不用此表）。 */
const NATIVE_INPUT_TYPES = {
  text: 'text',
  tel: 'tel',
  email: 'email',
  url: 'url',
  date: 'date',
  number: 'number',
};

/* 沿用引擎慣例的隨機 id（admin-form-edit.js ensureErrorEl 同式）。 */
function randomId() {
  return `f-${Math.random().toString(36).slice(2, 8)}`;
}

/* 把 spec 正規化：補 id（缺省=name → 再缺省=隨機）、決定 control tag 與 input--* 變體。 */
function normalize(spec) {
  const s = { ...spec };
  s.id = s.id || s.name || randomId();
  return s;
}

/**
 * 依 spec 蒐集 control（input/select/textarea）該有的屬性鍵值對（不含 class，class 另算）。
 * 回傳 { tag, attrs:Array<[k,v]>, options } —— attrs 用陣列保序，確保字串 / DOM 兩路產出一致順序。
 */
function buildControlAttrs(s) {
  /** @type {Array<[string,(string|true)]>} */
  const attrs = [];
  const push = (k, v) => { if (v != null && v !== false) attrs.push([k, v === true ? true : String(v)]); };

  let tag;
  let typeAttr = null;

  if (s.type === 'select') {
    tag = 'select';
  } else if (s.type === 'textarea') {
    tag = 'textarea';
  } else if (s.type === 'file') {
    tag = 'input';
    typeAttr = 'file';
  } else if (s.type === 'switch') {
    tag = 'input';
    typeAttr = 'checkbox';
  } else {
    tag = 'input';
    typeAttr = NATIVE_INPUT_TYPES[s.type] || 'text';
  }

  if (typeAttr) push('type', typeAttr);
  push('id', s.id);
  push('name', s.name);

  // 必填 → required + aria-required（沿用各 edit 頁慣例：兩者並存）
  if (s.required) {
    push('required', true);
    push('aria-required', 'true');
  }

  // 文字類值 / placeholder（textarea 的初值另以 textContent 放，不走 value 屬性）
  if (tag !== 'textarea' && tag !== 'select') {
    push('value', s.value);
  }
  if (s.placeholder != null && tag !== 'select') push('placeholder', s.placeholder);

  // HTML5 原生屬性（與規則層雙保險）
  push('autocomplete', s.autocomplete);
  push('inputmode', s.inputmode);
  push('minlength', s.minlength);
  push('maxlength', s.maxlength);
  push('min', s.min);
  push('max', s.max);
  push('step', s.step);
  if (tag === 'textarea') push('rows', s.rows);

  // file 專屬：accept（由 allowPdf / 傳入 accept 決定）
  if (s.type === 'file') {
    const accept = s.accept || (s.allowPdf
      ? 'image/jpeg,image/png,application/pdf'
      : 'image/jpeg,image/png');
    push('accept', accept);
  }

  // 宣告式規則插槽：自訂規則寫 data-rules，交 field-rules.js 解（factory 不解語義）。
  if (s.rules) push('data-rules', s.rules);
  // 規則夥伴 / 參數投影（field-rules.js 讀 dataset.ruleTarget / ruleWhen；checkbox-min 走 :arg 不需此）。
  if (s.ruleTarget) push('data-rule-target', s.ruleTarget);
  if (s.ruleWhen) push('data-rule-when', s.ruleWhen);
  if (s.ruleMin != null) push('data-rule-min', s.ruleMin);

  // aria-describedby：有 hint 預設指 hint（報錯時引擎切到 error）。
  if (s.hint) push('aria-describedby', `${s.id}-hint`);

  // 逃生艙：任意額外屬性（最後 push，可覆寫前面同名鍵的「最後一個 win」由消費端 DOM/字串各自處理）。
  if (s.attrs && typeof s.attrs === 'object') {
    Object.keys(s.attrs).forEach((k) => push(k, s.attrs[k]));
  }

  return { tag, attrs };
}

/* control 的 class 字串（form-field__input + 變體）。 */
function controlClass(s) {
  const cls = ['form-field__input'];
  if (s.type === 'select') cls.push('form-field__input--select');
  else if (s.type === 'textarea') cls.push('form-field__input--textarea');
  if (s.mono) cls.push('form-field__input--mono');
  return cls.join(' ');
}

/* .form-field 包裹的 class 字串。 */
function fieldClass(s) {
  const cls = ['form-field'];
  if (s.required) cls.push('form-field--required');
  if (s.className) cls.push(s.className);
  return cls.join(' ');
}

/* ====================================================================
 * DOM 模式
 * ==================================================================== */

/**
 * 產一個 .form-field 節點。
 * @param {object} spec 見檔頭 spec 欄位說明
 * @returns {HTMLElement}
 */
function formField(spec) {
  const s = normalize(spec);
  const wrap = document.createElement('div');
  wrap.className = fieldClass(s);
  // data-field 恆出（值＝name）：與 _form-field.html EJS 對齊，clinic-detail.js / 驗證器以 [data-field=NAME] 定位欄位。
  wrap.setAttribute('data-field', s.name || '');

  // slotBefore（欄位最前的自訂節點 / HTML 片段）
  appendSlot(wrap, s.slotBefore);

  // label
  if (s.label != null) {
    const label = document.createElement('label');
    label.className = 'form-field__label';
    label.setAttribute('for', s.id);
    label.textContent = s.label;
    wrap.appendChild(label);
  }

  // control
  const { tag, attrs } = buildControlAttrs(s);
  const control = document.createElement(tag);
  control.className = controlClass(s);
  attrs.forEach(([k, v]) => {
    if (v === true) control.setAttribute(k, '');
    else control.setAttribute(k, v);
  });

  // select options
  if (tag === 'select') {
    if (s.optionsPlaceholder != null) {
      const ph = document.createElement('option');
      ph.value = '';
      ph.disabled = true;
      if (s.value == null || s.value === '') ph.selected = true;
      ph.textContent = s.optionsPlaceholder;
      control.appendChild(ph);
    }
    (s.options || []).forEach(([v, t]) => {
      const opt = document.createElement('option');
      opt.value = v;
      opt.textContent = t;
      if (s.value != null && String(s.value) === String(v)) opt.selected = true;
      control.appendChild(opt);
    });
  } else if (tag === 'textarea') {
    if (s.value != null) control.textContent = String(s.value);
  }

  wrap.appendChild(control);

  // hint（有才產）
  if (s.hint) {
    const hint = document.createElement('p');
    hint.className = 'form-field__hint';
    hint.id = `${s.id}-hint`;
    hint.textContent = s.hint;
    wrap.appendChild(hint);
  }

  // error（恆產，預置該欄專屬措辭 errorText，hidden 由引擎切）
  const error = document.createElement('p');
  error.className = 'form-field__error';
  error.id = `${s.id}-error`;
  error.setAttribute('role', 'alert');
  error.hidden = true;
  if (s.errorText) error.textContent = s.errorText;
  wrap.appendChild(error);

  // slotAfter（欄位最末的自訂節點 / HTML 片段）
  appendSlot(wrap, s.slotAfter);

  return wrap;
}

/* slot 支援 string（innerHTML 片段，呼叫端自負安全）/ Node / Node[]。 */
function appendSlot(wrap, slot) {
  if (slot == null) return;
  if (typeof slot === 'string') {
    const tpl = document.createElement('template');
    tpl.innerHTML = slot;
    wrap.appendChild(tpl.content);
  } else if (slot instanceof Node) {
    wrap.appendChild(slot);
  } else if (Array.isArray(slot)) {
    slot.forEach((n) => n instanceof Node && wrap.appendChild(n));
  }
}

/* ====================================================================
 * 字串模式（innerHTML 模板）—— 所有插入值先 escapeHtml。
 * ==================================================================== */

/* 把 attrs 陣列轉成屬性字串（boolean 屬性只出鍵名）。值已於此 escapeHtml。 */
function attrsToString(attrs) {
  return attrs
    .map(([k, v]) => (v === true ? ` ${k}` : ` ${k}="${escapeHtml(v)}"`))
    .join('');
}

/**
 * 產 .form-field 的 HTML 字串（與 formField 同結構、同屬性序）。
 * @param {object} spec
 * @returns {string}
 */
function formFieldHTML(spec) {
  const s = normalize(spec);
  const { tag, attrs } = buildControlAttrs(s);
  const attrStr = attrsToString(attrs);

  const labelHTML = s.label != null
    ? `<label class="form-field__label" for="${escapeHtml(s.id)}">${escapeHtml(s.label)}</label>`
    : '';

  let controlHTML;
  if (tag === 'select') {
    let opts = '';
    if (s.optionsPlaceholder != null) {
      const sel = (s.value == null || s.value === '') ? ' selected' : '';
      opts += `<option value="" disabled${sel}>${escapeHtml(s.optionsPlaceholder)}</option>`;
    }
    (s.options || []).forEach(([v, t]) => {
      const sel = (s.value != null && String(s.value) === String(v)) ? ' selected' : '';
      opts += `<option value="${escapeHtml(v)}"${sel}>${escapeHtml(t)}</option>`;
    });
    controlHTML = `<select class="${controlClass(s)}"${attrStr}>${opts}</select>`;
  } else if (tag === 'textarea') {
    const body = s.value != null ? escapeHtml(s.value) : '';
    controlHTML = `<textarea class="${controlClass(s)}"${attrStr}>${body}</textarea>`;
  } else {
    controlHTML = `<input class="${controlClass(s)}"${attrStr} />`;
  }

  const hintHTML = s.hint
    ? `<p class="form-field__hint" id="${escapeHtml(s.id)}-hint">${escapeHtml(s.hint)}</p>`
    : '';

  const errorText = s.errorText ? escapeHtml(s.errorText) : '';
  const errorHTML = `<p class="form-field__error" id="${escapeHtml(s.id)}-error" role="alert" hidden>${errorText}</p>`;

  const before = typeof s.slotBefore === 'string' ? s.slotBefore : '';
  const after = typeof s.slotAfter === 'string' ? s.slotAfter : '';

  return `<div class="${fieldClass(s)}">${before}${labelHTML}${controlHTML}${hintHTML}${errorHTML}${after}</div>`;
}

/* ====================================================================
 * checkbox 群（.check-grid[role=group]）—— byte 等價 doctor-edit/clinic-edit 既有 markup。
 * ==================================================================== */

/**
 * 產 checkbox 群容器。
 * @param {object} spec
 * @param {string} spec.name        群內所有 checkbox 共用 name
 * @param {string} spec.ariaLabel   群的 aria-label（role=group 需要可及名稱）
 * @param {Array} spec.options      [[value, text, checked?], …]
 * @param {string} [spec.rules]     群層規則（如 "checkbox-min:1"）→ data-rules
 * @param {string} [spec.ruleMin]   → data-rule-min（checkbox-min 也可走 :arg，二擇一）
 * @param {string} [spec.id]        群容器 id（缺省=name → 隨機）
 * @param {string} [spec.className] 額外 class（如 check-grid__full）
 * @returns {HTMLElement}
 */
function formFieldGroup(spec) {
  const s = normalize(spec);
  const grid = document.createElement('div');
  const cls = ['check-grid'];
  if (s.className) cls.push(s.className);
  grid.className = cls.join(' ');
  grid.setAttribute('role', 'group');
  if (s.ariaLabel != null) grid.setAttribute('aria-label', s.ariaLabel);
  // 群層宣告式規則插槽（checkbox-min 等）；id 供錯誤錨點 / 引擎掃描。
  grid.id = s.id;
  grid.dataset.fieldGroup = '';
  if (s.rules) grid.dataset.rules = s.rules;
  if (s.ruleMin != null) grid.dataset.ruleMin = String(s.ruleMin);

  (s.options || []).forEach(([value, text, checked]) => {
    const label = document.createElement('label');
    label.className = 'form-check';

    const input = document.createElement('input');
    input.className = 'form-check__input';
    input.type = 'checkbox';
    input.name = s.name;
    input.value = value;
    if (checked) input.checked = true;

    const box = document.createElement('span');
    box.className = 'form-check__box';

    label.appendChild(input);
    label.appendChild(box);
    label.appendChild(document.createTextNode(text));
    grid.appendChild(label);
  });

  // 群尾預置 .form-field__error（群層驗證錯誤錨點，hidden 由引擎切）。
  const error = document.createElement('p');
  error.className = 'form-field__error';
  error.id = `${s.id}-error`;
  error.setAttribute('role', 'alert');
  error.hidden = true;
  if (s.errorText) error.textContent = s.errorText;
  grid.appendChild(error);

  return grid;
}

export { formField, formFieldHTML, formFieldGroup };
